import { useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { typographyVariants } from '@mint-lib/tw-components';
import { type Matcher } from '@mint-lib/tw-components/patterns';
import { isBefore, isSameDay, startOfToday } from 'date-fns';

import DatePicker from '../../components/DatePicker.jsx';
import { type FlowFormInput } from '../../types/singleFlow.js';

const DateRangeBoundary = () => {
  const { t } = useTranslation('@myn/approval_v2');
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');

  const { control, trigger } = useFormContext<FlowFormInput>();
  const dateFrom = useWatch({
    control,
    name: 'dateFrom',
  });
  const initialStartDateRef = useRef(dateFrom);

  const isPastExceptSelected = (): Matcher => {
    const today = startOfToday();

    return (date: Date) =>
      isBefore(date, today) && !isSameDay(date, initialStartDateRef.current);
  };

  return (
    <div>
      <h6 className={typographyVariants({ variant: 'h6', className: 'mb-4' })}>
        {t('Set validation dates')}
      </h6>
      <div className="grid gap-x-2 gap-y-3 md:grid-cols-2">
        <div>
          <Controller
            control={control}
            name="dateFrom"
            render={({ field, fieldState }) => {
              return (
                <DatePicker
                  id="approval-flow-start-date"
                  label={t('Start date{{mandatory}}', { mandatory: '*' })}
                  a11yLabel="Start date field"
                  value={field.value}
                  error={fieldState.error?.message}
                  disabled={isReadOnly}
                  disabledDates={isPastExceptSelected()}
                  clearable={false}
                  onApply={(date) => {
                    field.onChange(date);
                    trigger('dateTo');
                  }}
                />
              );
            }}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="dateTo"
            render={({ field, fieldState }) => {
              return (
                <DatePicker
                  id="approval-flow-end-date"
                  label={t('End date')}
                  a11yLabel="End date field"
                  value={field.value ?? undefined}
                  error={fieldState.error?.message}
                  disabled={isReadOnly}
                  clearable
                  onApply={(date) => {
                    field.onChange(date);
                    trigger('dateFrom');
                  }}
                  onClear={() => {
                    field.onChange(null);
                    trigger('dateFrom');
                  }}
                />
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DateRangeBoundary;
